import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';

const RelatedCities = ({ currentCity, region, cityData }) => {
  // Define city relationships and groupings
  const cityGroups = {
    // Coachella Valley Luxury/Resort Cities
    luxury: {
      title: "Luxury & Resort Communities",
      cities: ['palm-springs', 'palm-desert', 'la-quinta', 'rancho-mirage', 'indian-wells'],
      description: "Premium service for upscale resort and luxury communities"
    },
    
    // Business & Commercial Hubs
    business: {
      title: "Business & Commercial Centers", 
      cities: ['riverside', 'corona', 'temecula', 'palm-springs', 'palm-desert'],
      description: "Professional legal services for business districts and commercial hubs"
    },
    
    // Family & Residential Communities
    family: {
      title: "Family & Residential Communities",
      cities: ['murrieta', 'menifee', 'cathedral-city', 'moreno-valley', 'thousand-palms'],
      description: "Specialized service for family-oriented and residential communities"
    },
    
    // Tourism & Recreation Areas
    tourism: {
      title: "Tourism & Recreation Areas",
      cities: ['joshua-tree', 'palm-springs', 'temecula', 'indian-wells', 'twentynine-palms'],
      description: "Expert service for tourism industry and recreational destinations"
    },
    
    // Regional Hubs & Major Cities
    major: {
      title: "Regional Hubs & Major Cities",  
      cities: ['riverside', 'palm-springs', 'moreno-valley', 'corona', 'temecula', 'yucca-valley'],
      description: "Comprehensive service from major population and business centers"
    }
  };

  // Geographic regions
  const regions = {
    'coachella-valley': {
      title: "Coachella Valley Cities",
      cities: ['palm-springs', 'palm-desert', 'cathedral-city', 'desert-hot-springs', 'indio', 'coachella', 'la-quinta', 'rancho-mirage', 'indian-wells', 'thousand-palms']
    },
    'hi-desert': {
      title: "Hi-Desert & Morongo Basin",
      cities: ['twentynine-palms', 'yucca-valley', 'joshua-tree', 'morongo-valley', 'landers']
    },
    'central-riverside': {
      title: "Central Riverside County",
      cities: ['riverside', 'moreno-valley', 'corona', 'menifee', 'temecula', 'murrieta']
    }
  };

  // City display names and slugs
  const cityInfo = {
    'palm-springs': { name: 'Palm Springs', region: 'coachella-valley' },
    'palm-desert': { name: 'Palm Desert', region: 'coachella-valley' },
    'cathedral-city': { name: 'Cathedral City', region: 'coachella-valley' },
    'desert-hot-springs': { name: 'Desert Hot Springs', region: 'coachella-valley' },
    'indio': { name: 'Indio', region: 'coachella-valley' },
    'coachella': { name: 'Coachella', region: 'coachella-valley' },
    'la-quinta': { name: 'La Quinta', region: 'coachella-valley' },
    'rancho-mirage': { name: 'Rancho Mirage', region: 'coachella-valley' },
    'indian-wells': { name: 'Indian Wells', region: 'coachella-valley' },
    'thousand-palms': { name: 'Thousand Palms', region: 'coachella-valley' },
    'twentynine-palms': { name: 'Twentynine Palms', region: 'hi-desert' },
    'yucca-valley': { name: 'Yucca Valley', region: 'hi-desert' },
    'joshua-tree': { name: 'Joshua Tree', region: 'hi-desert' },
    'morongo-valley': { name: 'Morongo Valley', region: 'hi-desert' },
    'landers': { name: 'Landers', region: 'hi-desert' },
    'riverside': { name: 'Riverside', region: 'central-riverside' },
    'moreno-valley': { name: 'Moreno Valley', region: 'central-riverside' },
    'corona': { name: 'Corona', region: 'central-riverside' },
    'menifee': { name: 'Menifee', region: 'central-riverside' },
    'temecula': { name: 'Temecula', region: 'central-riverside' },
    'murrieta': { name: 'Murrieta', region: 'central-riverside' }
  };

  // Get current city's region
  const currentRegion = cityInfo[currentCity]?.region;
  
  // Get related cities from same region (excluding current city)
  const regionalCities = regions[currentRegion]?.cities.filter(city => city !== currentCity).slice(0, 5) || [];
  
  // Get cities from a thematic group that includes current city
  const getThematicCities = () => {
    for (const [groupKey, group] of Object.entries(cityGroups)) {
      if (group.cities.includes(currentCity)) {
        return {
          title: group.title,
          description: group.description,
          cities: group.cities.filter(city => city !== currentCity).slice(0, 4)
        };
      }
    }
    return null;
  };

  const thematicGroup = getThematicCities();

  // Get major hub cities (always show these for cross-regional linking)
  const majorHubs = ['riverside', 'palm-springs', 'yucca-valley'].filter(city => city !== currentCity);

  return (
    <section style={{ backgroundColor: 'var(--bg-card)', padding: '60px 0' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
            Related Legal Services Areas
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
            We also provide professional legal document services in these nearby communities and specialized areas.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minMax(350px, 1fr))', gap: '2rem' }}>
          
          {/* Regional Cities */}
          {regionalCities.length > 0 && (
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <MapPin size={20} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                {regions[currentRegion]?.title}
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                Professional legal document services throughout the region.
              </p>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {regionalCities.map(citySlug => (
                  <Link 
                    key={citySlug}
                    to={`/cities/${citySlug}`}
                    style={{ 
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      backgroundColor: 'var(--bg-card)',
                      border: '1px solid var(--border-light)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = 'var(--bg-hover)';
                      e.target.style.borderColor = 'var(--brand-primary)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'var(--bg-card)';
                      e.target.style.borderColor = 'var(--border-light)';
                    }}
                  >
                    <ArrowRight size={16} color="var(--brand-primary)" style={{ marginRight: '0.75rem' }} />
                    <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                      {cityInfo[citySlug]?.name} Process Server
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Thematic Group */}
          {thematicGroup && (
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                {thematicGroup.title}
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                {thematicGroup.description}
              </p>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {thematicGroup.cities.map(citySlug => (
                  <Link 
                    key={citySlug}
                    to={`/cities/${citySlug}`}
                    style={{ 
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      backgroundColor: 'var(--bg-card)',
                      border: '1px solid var(--border-light)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = 'var(--bg-hover)';
                      e.target.style.borderColor = 'var(--brand-primary)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'var(--bg-card)';
                      e.target.style.borderColor = 'var(--border-light)';
                    }}
                  >
                    <ArrowRight size={16} color="var(--brand-primary)" style={{ marginRight: '0.75rem' }} />
                    <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                      {cityInfo[citySlug]?.name} Legal Services
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Regional Hubs */}
          {majorHubs.length > 0 && (
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                Regional Service Hubs
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                Major regional centers with comprehensive legal document services.
              </p>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {majorHubs.map(citySlug => (
                  <Link 
                    key={citySlug}
                    to={`/cities/${citySlug}`}
                    style={{ 
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      backgroundColor: 'var(--bg-card)',
                      border: '1px solid var(--border-light)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = 'var(--bg-hover)';
                      e.target.style.borderColor = 'var(--brand-primary)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'var(--bg-card)';
                      e.target.style.borderColor = 'var(--border-light)';
                    }}
                  >
                    <ArrowRight size={16} color="var(--brand-primary)" style={{ marginRight: '0.75rem' }} />
                    <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                      {cityInfo[citySlug]?.name} Regional Hub
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Call to Action */}
        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <div style={{
            backgroundColor: 'var(--bg-page)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
              Need Service in Multiple Areas?
            </h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
              We provide comprehensive legal document services across all Southern California desert and inland regions.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order for Any City
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Get Multi-Area Quote
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RelatedCities;